import React from 'react';
import { useAudioWaveform } from '@livekit/components-react';
import { Flex, View } from '@smarterservices/smarter-ui';

export default function AudioVisualizer({
  track,
  barCount = 5,
  height = '16px',
  barWidth = '4px',
  activeColor = 'success',
  inactiveColor = 'secondary',
  minBarHeight = '10px',
}) {
  const waveform = useAudioWaveform(track, { duration: 350 });
  const [levels, setLevels] = React.useState(Array(barCount).fill(0));
  const prevLevels = React.useRef(levels);

  React.useEffect(() => {
    const bars = waveform?.bars;
    if (!bars?.length) return;

    // Get average level from waveform
    const avgLevel =
      bars.reduce((sum, val) => sum + Math.abs(val), 0) / bars.length;

    // Create a new array with random variations based on the average level
    const newLevels = Array(barCount)
      .fill(0)
      .map(() => {
        const randomFactor = 0.7 + Math.random() * 0.6;
        return Math.min(Math.max(avgLevel * randomFactor, 0.1), 1);
      });

    // Only update state if levels changed
    const same =
      prevLevels.current.length === newLevels.length &&
      newLevels.every((v, i) => v === prevLevels.current[i]);
    if (!same) {
      prevLevels.current = newLevels;
      setLevels(newLevels);
    }
  }, [waveform?.bars, barCount]);

  // If no track or no audio detected, show minimal bars
  if (!track || !waveform?.bars?.length) {
    return (
      <Flex
        as="div"
        alignItems="center"
        justifyContent="center"
        height={height}
      >
        {Array(barCount)
          .fill(0)
          .map((_, i) => (
            <View
              key={i}
              margin="none xx-small none none"
              width={barWidth}
              height={minBarHeight}
              background={inactiveColor}
              borderRadius="small"
            />
          ))}
      </Flex>
    );
  }

  return (
    <View as="div">
      <Flex
        as="div"
        alignItems="center"
        justifyContent="center"
        height={height}
      >
        {levels.map((level, i) => {
          // Calculate height based on level, but expand from center
          const barHeight = `${Math.max(level * 100, 20)}%`;

          return (
            <View
              key={i}
              margin="none xx-small none none"
              width={barWidth}
              position="relative"
              height="90%"
              display="flex"
              alignItems="center"
              themeOverride={{
                marginXxSmall: '2px',
              }}
            >
              <View
                width={barWidth}
                height={barHeight}
                background={activeColor}
                borderRadius="small"
                position="absolute"
                style={{
                  top: `${50 - parseInt(barHeight) / 2}%`,
                  transition: 'height 0.1s ease-out, top 0.1s ease-out',
                }}
              />
            </View>
          );
        })}
      </Flex>
    </View>
  );
}
